import React, { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { searchStocks } from "../utils/stockApi.js";
import { goToStock } from "../utils/route.js";

// Search box with live suggestions. Works the same for 100 or 12,000+ stocks
// because the lookup goes through stockApi.searchStocks (swap for a backend).
export default function CompanySearch({
  placeholder = "Search by company name or ticker",
  className = "",
  autoFocus = false,
  onSelect,
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    const id = ++seq.current;
    if (!q.trim()) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      searchStocks(q, 25).then((r) => {
        if (id === seq.current) {
          setResults(r);
          setActive(0);
        }
      });
    }, 120);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const close = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const choose = (ticker) => {
    setQ("");
    setResults([]);
    setOpen(false);
    if (onSelect) onSelect(ticker);
    goToStock(ticker);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) choose(results[active].ticker);
      else if (q.trim()) choose(q.trim().toUpperCase());
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={`co-search ${className}`} ref={boxRef}>
      <Search size={16} className="co-search-icon" />
      <input
        type="text"
        value={q}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        aria-label="Search companies"
      />
      {open && q.trim() && (
        <ul className="co-search-list">
          {results.length === 0 && <li className="co-search-empty">No matching companies</li>}
          {results.map((c, i) => (
            <li
              key={c.ticker}
              className={i === active ? "active" : ""}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(c.ticker);
              }}
            >
              <span className="co-search-ticker">{c.ticker}</span>
              <span className="co-search-name">{c.name}</span>
              <span className="co-search-ind">{c.industry}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
